import { useMemo, useRef, useEffect, CSSProperties } from "react";
import { ICONGRAPHY } from "../icongraphy.ts";

export interface LoaderProps {
  theme?: "light" | "dark";
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
}

function Loader({ theme = "dark", size = "sm" }: LoaderProps) {
  const iconSize = useMemo(() => ICONGRAPHY.size[size], [size]);

  const spinnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const spinner = spinnerRef.current;
    if (!spinner) return;

    const animation = spinner.animate(
      [
        { transform: "rotate(0deg)" },
        { transform: "rotate(180deg)" },
        { transform: "rotate(360deg)" },
      ],
      {
        duration: 1800,
        easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        iterations: Infinity,
      }
    );

    return () => {
      animation.cancel();
    };
  }, []);

  const containerStyle: CSSProperties = {
    width: iconSize,
    height: iconSize,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  };

  const spinnerStyle: CSSProperties = {
    width: iconSize,
    height: iconSize,
    position: "absolute",
  };

  const dotPositions = useMemo<Record<number, CSSProperties>>(
    () => ({
      0: { top: iconSize / 2 - 2, left: 0 },
      1: { top: 0, left: iconSize / 2 - 2 },
      2: { top: iconSize / 2 - 2, left: iconSize - 4 },
      3: { bottom: 0, left: iconSize / 2 - 2 },
    }),
    [iconSize]
  );
  
  return (
    <div style={containerStyle}>
      <div ref={spinnerRef} style={spinnerStyle}>
        {Array.from([0, 1, 2, 3]).map((index) => (
          <div
            key={index}
            style={{
              width: 4,
              height: 4,
              borderRadius: 6,
              backgroundColor: "#0946b8",
              position: "absolute",
              ...dotPositions[index],
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default Loader;
